import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { Reveal } from '@/components/motion/Reveal';
import {
  ActivityIcon,
  FilterIcon,
  FlameIcon,
  GlobeIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MegaphoneIcon,
  MonitorPlayIcon,
  RocketIcon,
  SettingsIcon,
  UsersIcon,
} from 'lucide-react';
import { useLayoutEffect, useRef, type ComponentType } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { DriftBadge } from '@/components/DriftBadge';
import { ProjectSwitcher } from '@/components/ProjectSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getConfig } from '@/config';
import type { Me } from '@/api/schemas';
import { useFilterLink } from '@/lib/params';
import type { Workspace } from '@/lib/workspace';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

function navItems(projectId: string, onboardingIncomplete: boolean): NavItem[] {
  const base = `/p/${encodeURIComponent(projectId)}`;
  const items: NavItem[] = [
    { to: base, label: 'Overview', icon: LayoutDashboardIcon, end: true },
    { to: `${base}/live`, label: 'Live', icon: ActivityIcon },
    { to: `${base}/people`, label: 'People', icon: UsersIcon },
    { to: `${base}/funnels`, label: 'Funnels', icon: FilterIcon },
    { to: `${base}/sessions`, label: 'Sessions', icon: MonitorPlayIcon },
    { to: `${base}/heatmaps`, label: 'Heatmaps', icon: FlameIcon },
    { to: `${base}/acquisition`, label: 'Acquisition', icon: MegaphoneIcon },
    { to: `${base}/audience`, label: 'Audience', icon: GlobeIcon },
    { to: `${base}/settings/keys`, label: 'Write keys', icon: KeyRoundIcon },
    { to: `${base}/settings/project`, label: 'Project', icon: SettingsIcon },
  ];
  // The install screen stays reachable forever, but it only earns a permanent
  // place in the navigation while it still has something to prove.
  if (onboardingIncomplete) {
    items.splice(1, 0, { to: `${base}/onboarding`, label: 'Install', icon: RocketIcon });
  }
  return items;
}

export function AppShell({ me, workspace }: { me: Me; workspace: Workspace }) {
  const { email, signOut } = useAuth();
  const location = useLocation();
  const contentScroll = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // Navigation starts at the page heading; changing filters keeps the reading position.
    if (contentScroll.current) contentScroll.current.scrollTop = 0;
  }, [location.pathname]);
  const config = getConfig();
  const onboardingIncomplete = workspace.project.first_event_at === null;
  // Every target in the shell keeps the environment and range you are looking
  // at. Without this, choosing Sessions while reading development quietly
  // moved you to production on the default range.
  const withFilters = useFilterLink();

  return (
    <div className="zs-product-shell flex flex-col bg-background">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:p-4">Skip to content</a>
      <header className="zs-topbar zs-dark-surface z-40">
        <div className="mx-auto flex min-h-16 flex-wrap py-3 max-w-[100rem] items-center gap-2 px-4 sm:px-6">
          <NavLink
            to={withFilters(`/p/${encodeURIComponent(workspace.project.project_id)}`)}
            className="flex items-center gap-2 rounded-md pr-1 text-sm font-semibold tracking-tight"
          >
            <img src="/brand/zeligate-mark.svg" alt="" className="size-7" />
            Zeligate
          </NavLink>

          {config.environment !== 'prod' ? (
            <Badge variant="outline" className="uppercase">
              {config.environment}
            </Badge>
          ) : null}

          <span className="mx-1 h-5 w-px bg-border" aria-hidden />

          <ProjectSwitcher me={me} workspace={workspace} />

          <div className="ml-auto flex items-center gap-2">
            <DriftBadge />
            <span className="hidden max-w-[14rem] truncate text-xs text-muted-foreground sm:inline">
              {email ?? ''}
            </span>
            <ThemeToggle />
            <Button variant="ghost" size="icon" onClick={signOut} aria-label="Sign out">
              <LogOutIcon className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="zs-workspace mx-auto flex w-full max-w-[100rem] flex-1 gap-4 p-3 sm:p-4">
        <nav aria-label="Project sections" className="zs-sidebar zs-dark-surface hidden w-52 shrink-0 md:block">
          <p className="zs-eyebrow px-3 pb-6 text-muted-foreground">Explore your product</p>
          <ul className="space-y-2">
            {navItems(workspace.project.project_id, onboardingIncomplete).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={withFilters(item.to)}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-full border border-transparent px-3 py-3 text-sm transition-colors',
                      isActive
                        ? 'bg-accent font-medium text-accent-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" aria-hidden />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Horizontal nav below the md breakpoint. Same targets, no drawer to
            get stuck open behind a chart. */}
        <div ref={contentScroll} className="zs-content-scroll min-w-0 flex-1 px-1 pb-4">
          <nav aria-label="Project sections" className="mb-4 md:hidden">
            <ul className="flex gap-1 overflow-x-auto pb-1">
              {navItems(workspace.project.project_id, onboardingIncomplete).map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={withFilters(item.to)}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-1.5 rounded-full border border-transparent px-3 py-3 text-sm whitespace-nowrap',
                        isActive
                          ? 'bg-accent font-medium text-accent-foreground'
                          : 'text-muted-foreground hover:bg-muted',
                      )
                    }
                  >
                    <item.icon className="size-4 shrink-0" aria-hidden />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <main id="main-content" className="min-w-0">
            {/* Keyed on the route so a navigation replays the entrance. Without
                the key the subtree is reused and the new screen simply appears,
                which is the thing that made moving between pages feel abrupt.
                Collapses to nothing under reduced motion. */}
            <Reveal key={location.pathname}>
              <Outlet />
            </Reveal>
          </main>
        </div>
      </div>
    </div>
  );
}
